import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, finalize, switchMap, takeUntil, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthStore, type AuthRefreshResult } from '../auth/state/auth.store';
import { shouldAttemptCookieRefresh } from '../auth/utils/auth-refresh-policy';
import { HttpCancelService } from '../services/http-cancel.service';

const REFRESH_RETRY_HEADER = 'x-auth-refresh-attempt';

const isPublicAuthEndpoint = (url: string): boolean =>
  url.includes('/api/auth/login') ||
  url.includes('/api/auth/google') ||
  url.includes('/api/auth/register') ||
  url.includes('/api/auth/confirm-email') ||
  url.includes('/api/auth/verify-otp') ||
  url.includes('/api/auth/forgot-password') ||
  url.includes('/api/auth/reset-password') ||
  url.includes('/api/auth/resend-confirmation');

const isAuthTokenEndpoint = (url: string): boolean =>
  url.includes('/api/auth/refresh') || url.includes('/api/auth/logout');

const isSameOriginApiCall = (reqUrl: string): boolean =>
  reqUrl.startsWith('/api/') ||
  reqUrl.startsWith(environment.apiUrl) ||
  reqUrl.startsWith('/v1/');

const isAppApiCall = (reqUrl: string): boolean =>
  reqUrl.startsWith('/api/') || reqUrl.startsWith(environment.apiUrl);

const isHealthEndpoint = (reqUrl: string): boolean =>
  reqUrl.includes('/api/health');

const shouldPreserveSessionForBackendConnectivityLoss = (
  reqUrl: string,
  error: HttpErrorResponse,
  hasLocalSession: boolean
): boolean =>
  error.status === 0 &&
  hasLocalSession &&
  isAppApiCall(reqUrl) &&
  !isHealthEndpoint(reqUrl) &&
  !isPublicAuthEndpoint(reqUrl) &&
  !reqUrl.includes('/api/auth/logout');

const isJivuOverlayRuntime = (): boolean => {
  if (typeof window === 'undefined') return false;

  const search = new URLSearchParams(window.location.search);
  return window.location.pathname.startsWith('/jivu-overlay') ||
    search.get('jivuNativeRoute') === 'overlay' ||
    document.documentElement.classList.contains('jivu-overlay-runtime') ||
    document.body.classList.contains('jivu-overlay-runtime');
};

const closeJivuOverlayRuntime = (): void => {
  if (typeof window === 'undefined') return;

  window.heyjivuDesktop?.overlay?.hide?.();
  window.Capacitor?.Plugins?.JivuOverlay?.hide?.();
  (window as any).JivuNativeOverlay?.hide?.();
};

const withApiHeaders = (req: HttpRequest<unknown>, token: string | null, isPublicEndpoint: boolean): HttpRequest<unknown> => {
  const isStreamEndpoint = req.url.includes('/stream');
  let request = req;

  if (isSameOriginApiCall(req.url)) {
    request = request.clone({ withCredentials: true });
  }

  if (!isStreamEndpoint) {
    const headers: { [name: string]: string } = {
      'X-App-Version': '21.0.0'
    };

    if (token && !isPublicEndpoint) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    request = request.clone({ setHeaders: headers });
  }

  return request;
};

export const callInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const injector = inject(Injector);
  const cancelService = injector.get(HttpCancelService);
  const authStore = injector.get(AuthStore);

  const isExternalUrl = req.url.startsWith('http') &&
    !req.url.includes('localhost') &&
    !req.url.includes(window.location.host) &&
    !req.url.startsWith(environment.apiUrl);

  if (isExternalUrl) {
    return next(req);
  }

  const isAuthEndpoint = isPublicAuthEndpoint(req.url);
  const requiresToken = !isAuthEndpoint && !isAuthTokenEndpoint(req.url);
  const token = authStore.token();
  const requestWithHeaders = withApiHeaders(req, requiresToken ? token : null, isAuthEndpoint);

  const attemptRefreshThenRetry = (): ReturnType<typeof next> => {
    const retryWithCurrentToken = (refreshResult?: AuthRefreshResult): ReturnType<typeof next> => {
      const currentToken = authStore.token();
      if (!currentToken) {
        if (isJivuOverlayRuntime()) {
          closeJivuOverlayRuntime();
        } else if (refreshResult?.sessionExpired) {
          authStore.expireSession();
        }
        return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
      }

      const refreshedRequest = withApiHeaders(
        requestWithHeaders.clone({
          setHeaders: {
            [REFRESH_RETRY_HEADER]: '1'
          }
        }),
        currentToken,
        isAuthEndpoint
      );

      return next(refreshedRequest).pipe(
        takeUntil(cancelService.getCancelObservable()),
        catchError((retryError) => {
          if (retryError instanceof HttpErrorResponse && retryError.status === 401 && isJivuOverlayRuntime()) {
            closeJivuOverlayRuntime();
          }
          return throwError(() => retryError);
        })
      );
    };

    return authStore.refreshSessionFromCookie().pipe(
      switchMap((refreshResult) => retryWithCurrentToken(refreshResult)),
      catchError(() => {
        return retryWithCurrentToken();
      })
    );
  };

  return next(requestWithHeaders).pipe(
    takeUntil(cancelService.getCancelObservable()),
    catchError((error: HttpErrorResponse) => {
      if (shouldPreserveSessionForBackendConnectivityLoss(req.url, error, Boolean(authStore.user() || authStore.token()))) {
        return throwError(() => error);
      }

      const shouldTryRefresh = shouldAttemptCookieRefresh({
        status: error.status,
        isPublicAuthEndpoint: isAuthEndpoint,
        isAuthTokenEndpoint: isAuthTokenEndpoint(req.url),
        refreshAlreadyAttempted: requestWithHeaders.headers.has(REFRESH_RETRY_HEADER)
      });

      if (shouldTryRefresh) {
        return attemptRefreshThenRetry();
      }

      if (error.status === 401 && !isAuthEndpoint && !isAuthTokenEndpoint(req.url) && isJivuOverlayRuntime()) {
        closeJivuOverlayRuntime();
      }

      return throwError(() => error);
    }),
    finalize(() => {
      // interceptor completed
    })
  );
};
